import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { getMeetingFeedbackAdmin } from './meetingFeedbackApi';
import FeedbackFormViewer from './FeedbackFormViewer';

// Which of the two forms in the client's feedback HTML a submission came from.
const FORM_LABEL = {
  parents: 'Took the test',
  guests: 'Watched the reveal',
};

function FormBadge({ formType }) {
  if (!FORM_LABEL[formType]) return null;
  return (
    <span
      style={{
        backgroundColor: formType === 'parents' ? '#FCE7F3' : '#E0F2FE',
        color: '#101828',
        borderRadius: '1rem',
        fontSize: '0.72rem',
        fontWeight: 600,
        padding: '0.15rem 0.6rem',
        whiteSpace: 'nowrap',
      }}
    >
      {FORM_LABEL[formType]}
    </span>
  );
}

/* Attendee list for one meeting, and the read-only form for whichever attendee
   the admin picks. Mirrors the host-side modal in the web portal. */
function FeedbackModal({ isOpen, onClose, meetingId, hostId, meetingTitle }) {
  const [loading, setLoading] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !meetingId) return;
    setSelected(null);
    setError('');
    setLoading(true);
    getMeetingFeedbackAdmin(meetingId, hostId).then((res) => {
      setLoading(false);
      if (res.success) setFeedbackList(res.feedback);
      else {
        setFeedbackList([]);
        setError(res.message || 'Failed to load feedback.');
      }
    });
  }, [isOpen, meetingId, hostId]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      /* The read-only form needs the room the attendee list does not. */
      width={selected ? 1140 : 800}
      centered
      styles={{ body: { paddingTop: 8 } }}
    >
      {selected ? (
        <>
          <button
            type="button"
            onClick={() => setSelected(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#101828',
              fontWeight: 600,
              fontSize: '0.85rem',
              padding: 0,
              marginBottom: 16,
              cursor: 'pointer',
            }}
          >
            ← Back to attendees
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <h5 style={{ color: '#101828', fontWeight: 700, margin: 0 }}>{selected.name || '—'}</h5>
            <FormBadge formType={selected.formType} />
          </div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 16, overflowWrap: 'anywhere' }}>
            {selected.email}
          </p>

          {selected.answers?.length ? (
            <FeedbackFormViewer key={selected.id} entry={selected} height="68vh" />
          ) : (
            <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>No answers were recorded.</p>
          )}
        </>
      ) : (
        <>
          <h5 style={{ color: '#101828', fontWeight: 700, marginBottom: 4 }}>Feedback</h5>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 20 }}>{meetingTitle}</p>

          {loading ? (
            <p style={{ color: '#6b7280', fontSize: '0.9rem', textAlign: 'center', padding: '1.5rem 0' }}>
              Loading feedback…
            </p>
          ) : error ? (
            <p style={{ color: '#dc2626', fontSize: '0.85rem' }}>{error}</p>
          ) : feedbackList.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: '0.9rem', textAlign: 'center', padding: '1.5rem 0' }}>
              No feedback submitted yet.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '60vh', overflowY: 'auto' }}>
              {feedbackList.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 8,
                    backgroundColor: '#EFF9FF',
                    border: '1px solid #88A7B2',
                    borderRadius: '1rem',
                    padding: '0.75rem 1rem',
                  }}
                >
                  {/* minWidth:0 lets this shrink past the intrinsic width of a
                      long email — without it the row grows and pushes the
                      button outside the modal. */}
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: '#101828', fontSize: '0.92rem' }}>
                        {entry.name || '—'}
                      </span>
                      <FormBadge formType={entry.formType} />
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.82rem', overflowWrap: 'anywhere' }}>
                      {entry.email}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelected(entry)}
                    style={{
                      backgroundColor: '#CAEBCC',
                      color: 'black',
                      border: 'none',
                      borderRadius: '1rem',
                      padding: '0.25rem 0.9rem',
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      cursor: 'pointer',
                    }}
                  >
                    Show Feedback
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export default FeedbackModal;
